"use client";

import {NeoModal} from "@/components/@modal/neo.modal";
import {NeoButton} from "@/components/forms";
import {useNeoContext} from "@/context/neo/neo.context";
import {userService} from "@/service/users.service";
import {vehicleService} from "@/service/vehicle.service";
import {yupResolver} from "@hookform/resolvers/yup";
import {useEffect, useMemo, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {AiOutlineLoading} from "react-icons/ai";
import {IoAddOutline} from "react-icons/io5";
import Select from "react-select";
import * as yup from "yup";

type VehicleMappingItem = {
  id: string;
  trackerid: string;
  routeId: string;
  tripType: "pickup" | "drop";
};

type SelectOption = {
  value: string;
  label: string;
};

type VehicleOption = {
  trackerId: string;
  trackerName: string;
};

type RouteOption = {
  routeId: string;
  routeName: string;
  vehicleName?: string;
  trackerId?: string;
  selectKey?: string;
};

type VehicleRoutes = {
  [key: string]: RouteOption[];
};

type VehicleMappingForm = {
  mappings: VehicleMappingItem[];
};

type EditVehicleMappingDetailsModalData = {
  client: Client;
  items: UserDetailsData;
  userId: string;
  onClose: () => void;
  onLocalUpdate?: (updatedUserData: UserDetailsData) => Promise<void>;
  onRefresh?: () => Promise<void>;
  routeOptions: SelectOption[];
  loadingRoutes: boolean;
};

type SelectOptionState = {
  isFocused: boolean;
};

const MAX_VEHICLE_MAPPINGS = 2;
const MIN_VEHICLE_MAPPINGS = 1;

const validationSchema = yup.object().shape({
  mappings: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string().required(),
        trackerid: yup
          .string()
          .default("")
          .test(
            "pickup-or-drop-required",
            "Please select at least one (Pickup or Drop) vehicle",
            function (value) {
              if (value) return true;

              const root = this.from?.[this.from.length - 1]?.value;
              const mappings: VehicleMappingItem[] = root?.mappings || [];

              return mappings.some(mapping => mapping?.trackerid);
            }
          ),
        routeId: yup.string().default(""),
        tripType: yup
          .mixed<"pickup" | "drop">()
          .oneOf(["pickup", "drop"])
          .required()
      })
    )
    .min(1, "At least one vehicle should be mapped.")
    .max(2, "You can only add up to 1 pickup and 1 drop vehicle")
    .test(
      "trip-type-limit",
      "You can only select 1 pickup and 1 drop vehicle",
      mappings => {
        const list = mappings || [];
        const pickupCount = list.filter(m => m?.tripType === "pickup").length;
        const dropCount = list.filter(m => m?.tripType === "drop").length;

        return pickupCount <= 1 && dropCount <= 1;
      }
    )
    .required()
});

export const EditVehicleMappingDetailsModal = ({
  data
}: {
  data: EditVehicleMappingDetailsModalData;
}) => {
  const {vehicles} = useNeoContext();

  const {
    client,
    items,
    userId,
    onClose,
    onLocalUpdate,
    onRefresh,
    routeOptions,
    loadingRoutes
  } = data;

  const [savingVehicleMappings, setSavingVehicleMappings] = useState(false);
  const [loadingVehicleRouteData, setLoadingVehicleRouteData] = useState(false);
  const [vehicleError, setVehicleError] = useState<string | null>(null);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationSuccess, setConfirmationSuccess] = useState(false);
  const [closeParentOnConfirmation, setCloseParentOnConfirmation] =
    useState(false);
  const [announcementVehicles, setAnnouncementVehicles] = useState<
    VehicleOption[]
  >([]);
  const [vehicleRoutes, setVehicleRoutes] = useState<VehicleRoutes>({});

  const initialMappings = useMemo(
    () => buildInitialVehicleMappings(items),
    [items]
  );

  const initialMappingById = useMemo(() => {
    return new Map(initialMappings.map(item => [item.id, item]));
  }, [initialMappings]);

  const vehicleOptions = useMemo(() => {
    if (announcementVehicles.length > 0) {
      return announcementVehicles.map(vehicle => ({
        value: String(vehicle.trackerId),
        label: vehicle.trackerName
      }));
    }

    return (vehicles || []).map(vehicle => ({
      value: String(vehicle.trackerId),
      label: vehicle.trackerName
    }));
  }, [announcementVehicles, vehicles]);

  const allRouteOptions = useMemo(() => {
    const routeMap = new Map<string, SelectOption>();

    Object.values(vehicleRoutes)
      .flat()
      .forEach(route => {
        if (!route.routeId) return;

        routeMap.set(String(route.routeId), {
          value: String(route.routeId),
          label: route.routeName || "No Route"
        });
      });

    routeOptions.forEach(option => {
      if (!routeMap.has(String(option.value))) {
        routeMap.set(String(option.value), option);
      }
    });

    return Array.from(routeMap.values());
  }, [routeOptions, vehicleRoutes]);

  const {control, handleSubmit, watch, reset, setValue} =
    useForm<VehicleMappingForm>({
      defaultValues: {
        mappings: initialMappings
      },
      resolver: yupResolver(validationSchema),
      mode: "onChange"
    });

  const watchedMappings = watch("mappings");
  const vehicleMappings = useMemo(
    () => watchedMappings || [],
    [watchedMappings]
  );

  const duplicateError = useMemo(
    () => getDuplicateVehicleError(vehicleMappings),
    [vehicleMappings]
  );

  const duplicateIndexes = useMemo(
    () => getDuplicateVehicleIndexes(vehicleMappings),
    [vehicleMappings]
  );

  useEffect(() => {
    setVehicleError(duplicateError);
  }, [duplicateError]);

  useEffect(() => {
    reset({
      mappings: initialMappings
    });
  }, [initialMappings, reset]);

  useEffect(() => {
    const fetchVehicleRouteData = async () => {
      if (!client?.institutionId) {
        setAnnouncementVehicles([]);
        setVehicleRoutes({});
        return;
      }

      setLoadingVehicleRouteData(true);

      try {
        const vehicles = await vehicleService.onGetInstitutionVehicles(
          Number(client.institutionId)
        );

        setAnnouncementVehicles(
          vehicles.map(vehicle => ({
            trackerId: vehicle.trackerId,
            trackerName: vehicle.trackerName
          }))
        );
        setVehicleRoutes({});
      } catch {
        setAnnouncementVehicles([]);
        setVehicleRoutes({});
      } finally {
        setLoadingVehicleRouteData(false);
      }
    };

    fetchVehicleRouteData();
  }, [client?.institutionId]);

  const isAddVehicleDisabled =
    vehicleMappings.length >= MAX_VEHICLE_MAPPINGS || savingVehicleMappings;

  const isOnlyOneVehicleLeft = vehicleMappings.length <= MIN_VEHICLE_MAPPINGS;

  const isSaveVehicleDisabled =
    !client?.institutionId ||
    savingVehicleMappings ||
    loadingVehicleRouteData ||
    vehicleMappings.length < MIN_VEHICLE_MAPPINGS ||
    !vehicleMappings.some(item => item.trackerid) ||
    !!duplicateError;

  const getVehicleCardTitle = (mapping: VehicleMappingItem) => {
    const initialTrackerid = initialMappingById.get(mapping.id)?.trackerid;

    if (initialTrackerid) {
      return (
        getTrackerName(vehicleOptions, initialTrackerid) || "Select Vehicle"
      );
    }

    return (
      getTrackerName(vehicleOptions, mapping.trackerid) || "Select Vehicle"
    );
  };

  const handleAddVehicleMapping = () => {
    if (vehicleMappings.length >= MAX_VEHICLE_MAPPINGS) return;

    const nextTripType = vehicleMappings.some(
      mapping => mapping.tripType === "pickup"
    )
      ? "drop"
      : "pickup";

    setValue(
      "mappings",
      [
        ...vehicleMappings,
        {
          id: `${Date.now()}-${vehicleMappings.length}`,
          trackerid: "",
          routeId: "",
          tripType: nextTripType
        }
      ],
      {shouldValidate: true}
    );

    setVehicleError(null);
  };

  const isTripTypeTakenElsewhere = (
    mappingIndex: number,
    tripType: "pickup" | "drop"
  ) =>
    vehicleMappings.some(
      (mapping, index) => index !== mappingIndex && mapping.tripType === tripType
    );

  const handleUpdateVehicleMapping = (
    mappingIndex: number,
    field: "trackerid" | "routeId" | "tripType",
    value: string
  ) => {
    const updatedMappings = vehicleMappings.map((item, index) =>
      index === mappingIndex ? {...item, [field]: value} : item
    );

    setValue("mappings", updatedMappings, {shouldValidate: true});
    setVehicleError(getDuplicateVehicleError(updatedMappings));
  };

  const handleVehicleChange = (mappingIndex: number, trackerId: string) => {
    const updatedMappings = vehicleMappings.map((item, index) => {
      if (index !== mappingIndex) return item;

      const availableRoutes = getRoutesForVehicle(trackerId, vehicleRoutes);
      const hasRoute = availableRoutes.some(
        option => String(option.routeId) === String(item.routeId)
      );

      return {
        ...item,
        trackerid: trackerId,
        routeId: hasRoute ? item.routeId : ""
      };
    });

    setValue("mappings", updatedMappings, {shouldValidate: true});
    setVehicleError(getDuplicateVehicleError(updatedMappings));
  };

  const handleRemoveVehicleMapping = (mappingIndex: number) => {
    if (vehicleMappings.length <= MIN_VEHICLE_MAPPINGS) return;

    const updatedMappings = vehicleMappings.filter(
      (_item, index) => index !== mappingIndex
    );
    setValue("mappings", updatedMappings, {shouldValidate: true});
    setVehicleError(null);
  };

  const handleUndoVehicleChanges = () => {
    reset({
      mappings: initialMappings
    });
    setVehicleError(null);
  };

  const handleCloseVehicleModal = () => {
    handleUndoVehicleChanges();
    onClose();
  };

  const handleCloseConfirmationModal = async () => {
    setShowConfirmationModal(false);
    setConfirmationMessage("");

    if (confirmationSuccess) {
      await onRefresh?.();
    }

    if (closeParentOnConfirmation) {
      setCloseParentOnConfirmation(false);
      onClose();
    }
  };

  const handleSaveVehicleMappings = async (formValues: VehicleMappingForm) => {
    const mappings = formValues.mappings || [];

    if (!client?.institutionId) {
      setVehicleError("Institution ID is required to update vehicle mappings.");
      return;
    }

    const currentDuplicateError = getDuplicateVehicleError(mappings);

    if (currentDuplicateError) {
      setVehicleError(currentDuplicateError);
      return;
    }

    const requestPayload: UserMappingRequest = {
      forcedMapping: false,
      mappingRequests: mappings
        .filter(mapping => mapping.trackerid)
        .map(mapping => ({
          trackerId: Number(mapping.trackerid),
          tripType: getTripTypeValue(mapping.tripType),
          userId,
          routeId: Number(mapping.routeId || 0)
        }))
    };

    setSavingVehicleMappings(true);
    setVehicleError(null);

    try {
      const response: UserMappingResponse =
        await userService.userMappingOnEdit(requestPayload);

      if (response?.status === "success") {
        await onLocalUpdate?.(
          buildUpdatedUserData(items, mappings, vehicleOptions, allRouteOptions)
        );

        setVehicleError(null);
        setConfirmationSuccess(true);
        setConfirmationMessage(
          response?.message || "Vehicle mapping updated successfully"
        );
        setCloseParentOnConfirmation(true);
        setShowConfirmationModal(true);
      } else {
        const failureMessage = "Vehicle mapping updated unsuccessfully";

        setConfirmationSuccess(false);
        setVehicleError(failureMessage);
        setConfirmationMessage(failureMessage);
        setCloseParentOnConfirmation(false);
        setShowConfirmationModal(true);
      }
    } catch {
      const failureMessage = "Vehicle mapping updated unsuccessfully";

      setConfirmationSuccess(false);
      setVehicleError(failureMessage);
      setConfirmationMessage(failureMessage);
      setCloseParentOnConfirmation(false);
      setShowConfirmationModal(true);
    } finally {
      setSavingVehicleMappings(false);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-4 px-5 py-5">
        <div className="flex items-center justify-between border-b border-default-200 pb-3 pr-5">
          <div>
            <h2 className="text-lg font-semibold">Edit Vehicle Mapping</h2>
          </div>
          <div className="flex justify-end gap-3">
            <NeoButton
              size="xs"
              className="border border-default-200 bg-white px-4 font-medium text-default-700"
              onPress={handleUndoVehicleChanges}
              isDisabled={savingVehicleMappings}
            >
              Undo
            </NeoButton>

            <NeoButton
              size="xs"
              className="bg-black px-4 text-white data-[disabled=true]:!cursor-not-allowed data-[disabled=true]:opacity-40"
              startContent={<IoAddOutline className="text-xl" />}
              onPress={handleAddVehicleMapping}
              isDisabled={isAddVehicleDisabled}
              style={{cursor: isAddVehicleDisabled ? "not-allowed" : "pointer"}}
            >
              Add Vehicle
            </NeoButton>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {vehicleMappings.map((mapping, index) => (
            <div
              key={mapping.id}
              className={`rounded-2xl border p-4 shadow-sm ${
                duplicateIndexes.has(index)
                  ? "border-danger"
                  : "border-default-200 bg-white"
              }`}
            >
              <div className="mb-4 flex items-start justify-between gap-3">
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 text-warning">
                    <div>
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8.79596 0C13.4138 0 16.4924 1.20944 16.4924 2.74874V3.29848V4.39798C17.1006 4.39798 17.5919 4.88931 17.5919 5.49747V7.69646C17.5919 8.30462 17.1006 8.79596 16.4924 8.79596V14.2934C16.4924 14.9016 16.0011 15.3929 15.3929 15.3929V16.4924C15.3929 17.1006 14.9016 17.5919 14.2934 17.5919H13.1939C12.5858 17.5919 12.0944 17.1006 12.0944 16.4924V15.3929H5.49747V16.4924C5.49747 17.1006 5.00614 17.5919 4.39798 17.5919H3.29848C2.69033 17.5919 2.19899 17.1006 2.19899 16.4924V15.3929C1.59083 15.3929 1.09949 14.9016 1.09949 14.2934V8.79596C0.491337 8.79596 0 8.30462 0 7.69646V5.49747C0 4.88931 0.491337 4.39798 1.09949 4.39798V3.29848V2.74874C1.09949 1.20944 4.17808 0 8.79596 0ZM3.29848 5.49747V8.79596C3.29848 9.40411 3.78982 9.89545 4.39798 9.89545H8.24621V4.39798H4.39798C3.78982 4.39798 3.29848 4.88931 3.29848 5.49747ZM9.3457 9.89545H13.1939C13.8021 9.89545 14.2934 9.40411 14.2934 8.79596V5.49747C14.2934 4.88931 13.8021 4.39798 13.1939 4.39798H9.3457V9.89545ZM3.84823 13.7437C4.13983 13.7437 4.4195 13.6278 4.62569 13.4216C4.83189 13.2155 4.94773 12.9358 4.94773 12.6442C4.94773 12.3526 4.83189 12.0729 4.62569 11.8667C4.4195 11.6605 4.13983 11.5447 3.84823 11.5447C3.55663 11.5447 3.27697 11.6605 3.07077 11.8667C2.86458 12.0729 2.74874 12.3526 2.74874 12.6442C2.74874 12.9358 2.86458 13.2155 3.07077 13.4216C3.27697 13.6278 3.55663 13.7437 3.84823 13.7437ZM13.7437 13.7437C14.0353 13.7437 14.3149 13.6278 14.5211 13.4216C14.7273 13.2155 14.8432 12.9358 14.8432 12.6442C14.8432 12.3526 14.7273 12.0729 14.5211 11.8667C14.3149 11.6605 14.0353 11.5447 13.7437 11.5447C13.4521 11.5447 13.1724 11.6605 12.9662 11.8667C12.76 12.0729 12.6442 12.3526 12.6442 12.6442C12.6442 12.9358 12.76 13.2155 12.9662 13.4216C13.1724 13.6278 13.4521 13.7437 13.7437 13.7437ZM12.0944 2.74874C12.0944 2.44638 11.8471 2.19899 11.5447 2.19899H6.04722C5.74486 2.19899 5.49747 2.44638 5.49747 2.74874C5.49747 3.0511 5.74486 3.29848 6.04722 3.29848H11.5447C11.8471 3.29848 12.0944 3.0511 12.0944 2.74874Z"
                          fill="#FFC727"
                        />
                      </svg>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <div className="text-sm font-semibold text-default-800">
                      {getVehicleCardTitle(mapping)}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  className={
                    isOnlyOneVehicleLeft
                      ? "cursor-not-allowed text-default-300"
                      : "text-danger"
                  }
                  onClick={() => {
                    if (!isOnlyOneVehicleLeft) {
                      handleRemoveVehicleMapping(index);
                    }
                  }}
                  disabled={isOnlyOneVehicleLeft || savingVehicleMappings}
                >
                  <div>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M11.1899 5.13861L8.5092 7.81814L11.1899 10.4998C11.2806 10.5916 11.3313 10.7157 11.3309 10.8447C11.3305 10.9738 11.279 11.0975 11.1878 11.1888C11.0966 11.2802 10.9729 11.3317 10.8438 11.3322C10.7147 11.3327 10.5907 11.2821 10.4987 11.1915L7.81799 8.50988L5.13724 11.1915C5.09194 11.2374 5.038 11.2739 4.97853 11.2989C4.91906 11.3238 4.85524 11.3368 4.79074 11.337C4.72625 11.3372 4.66235 11.3246 4.60273 11.3C4.54311 11.2754 4.48894 11.2393 4.44335 11.1937C4.39776 11.148 4.36165 11.0938 4.33709 11.0342C4.31254 10.9746 4.30002 10.9106 4.30027 10.8462C4.30052 10.7817 4.31353 10.7178 4.33854 10.6584C4.36356 10.5989 4.40009 10.545 4.44603 10.4998L7.12678 7.81814L4.44603 5.13861C4.40009 5.09334 4.36356 5.03942 4.33854 4.97998C4.31353 4.92053 4.30052 4.85672 4.30027 4.79222C4.30002 4.72772 4.31254 4.66381 4.33709 4.60417C4.36165 4.54453 4.39776 4.49034 4.44335 4.44472C4.48894 4.39909 4.54311 4.36294 4.60273 4.33834C4.66235 4.31374 4.72625 4.30118 4.79074 4.30138C4.85524 4.30158 4.91906 4.31454 4.97853 4.33951C5.038 4.36448 5.09194 4.40097 5.13724 4.44687L7.81799 7.12641L10.4987 4.44687C10.5907 4.35627 10.7147 4.3057 10.8438 4.3062C10.9729 4.30669 11.0966 4.35822 11.1878 4.44953C11.279 4.54085 11.3305 4.66454 11.3309 4.79363C11.3313 4.92272 11.2806 5.04673 11.1899 5.13861ZM12.6556 12.6556C13.6123 11.6988 14.2638 10.4798 14.5278 9.15276C14.7917 7.8257 14.6562 6.45018 14.1384 5.20013C13.6206 3.95008 12.7437 2.88165 11.6187 2.12994C10.4937 1.37823 9.17104 0.977009 7.81799 0.977009C6.46494 0.977009 5.14227 1.37823 4.01725 2.12994C2.89223 2.88165 2.01537 3.95008 1.49756 5.20013C0.979755 6.45018 0.844253 7.8257 1.10819 9.15276C1.37213 10.4798 2.02365 11.6988 2.98038 12.6556C4.26425 13.937 6.00407 14.6567 7.81799 14.6567C9.63191 14.6567 11.3717 13.937 12.6556 12.6556ZM7.81799 3.54226e-05C6.27173 0.000262509 4.76025 0.45894 3.47464 1.31808C2.18903 2.17722 1.18701 3.39825 0.595253 4.8268C0.00349961 6.25535 -0.151415 7.82728 0.150093 9.34386C0.451602 10.8604 1.196 12.2536 2.28917 13.3472C3.75557 14.8135 5.74442 15.6373 7.8182 15.6373C9.89197 15.6372 11.8808 14.8134 13.3472 13.347C14.8135 11.8806 15.6373 9.89171 15.6373 7.81793C15.6372 5.74415 14.8134 3.75532 13.347 2.28896C12.6226 1.56073 11.7609 0.983444 10.8118 0.590544C9.86275 0.197643 8.84516 -0.00306695 7.81799 3.54226e-05Z"
                        fill="#FF3131"
                      />
                    </svg>
                  </div>
                </button>
              </div>
              <div className="mt-4">Trip Type</div>

              <div className="mt-2 flex gap-2">
                <TripTypeChip
                  active={mapping.tripType === "pickup"}
                  label="Pickup"
                  disabled={
                    savingVehicleMappings ||
                    isTripTypeTakenElsewhere(index, "pickup")
                  }
                  onClick={() =>
                    handleUpdateVehicleMapping(index, "tripType", "pickup")
                  }
                />

                <TripTypeChip
                  active={mapping.tripType === "drop"}
                  label="Drop"
                  disabled={
                    savingVehicleMappings ||
                    isTripTypeTakenElsewhere(index, "drop")
                  }
                  onClick={() =>
                    handleUpdateVehicleMapping(index, "tripType", "drop")
                  }
                />
              </div>
              <div className="mt-4 flex flex-col gap-2">
                <Controller
                  name={`mappings.${index}.trackerid`}
                  control={control}
                  render={({field, fieldState}) => (
                    <div className="flex flex-col gap-1">
                      <label htmlFor={`trackerid-${mapping.id}`}>
                        Select Vehicle <span className="text-danger">*</span>
                      </label>

                      <Select
                        inputId={`trackerid-${mapping.id}`}
                        name={field.name}
                        options={vehicleOptions}
                        getOptionLabel={option => option.label}
                        getOptionValue={option => String(option.value)}
                        value={
                          field.value
                            ? (vehicleOptions.find(
                                option =>
                                  String(option.value) === String(field.value)
                              ) ?? null)
                            : null
                        }
                        onChange={selectedOption => {
                          const nextTrackerId = selectedOption
                            ? String(selectedOption.value)
                            : "";

                          field.onChange(nextTrackerId);
                          handleVehicleChange(index, nextTrackerId);
                        }}
                        placeholder="Select Vehicle"
                        isClearable
                        required
                        aria-required="true"
                        isDisabled={
                          savingVehicleMappings || loadingVehicleRouteData
                        }
                        maxMenuHeight={120}
                        // styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                          ...selectStyles,
                          menuPortal: (base: any) => ({
                            ...base,
                            zIndex: 9999
                          })
                        }}
                      />

                      {fieldState.error && (
                        <p className="mt-1 text-xs text-pink-600">
                          {fieldState.error.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>

              <div className="mt-4 flex flex-col gap-2">
                <Controller
                  name={`mappings.${index}.routeId`}
                  control={control}
                  render={({field}) => {
                    const filteredRoutes = getRoutesForVehicle(
                      mapping.trackerid,
                      vehicleRoutes
                    );

                    const selectedRoute =
                      filteredRoutes.find(
                        option => String(option.routeId) === String(field.value)
                      ) || null;

                    return (
                      <></>
                      // <div className="flex flex-col gap-1">
                      //   <label htmlFor={`routeId-${mapping.id}`}>
                      //     Select Route (optional)
                      //   </label>

                      //   <Select
                      //     inputId={`routeId-${mapping.id}`}
                      //     name={field.name}
                      //     options={filteredRoutes}
                      //     getOptionLabel={option =>
                      //       option.routeName || "No Route"
                      //     }
                      //     getOptionValue={option =>
                      //       String(option.routeId ?? option.selectKey)
                      //     }
                      //     value={selectedRoute}
                      //     onChange={selectedOption => {
                      //       const nextRouteId = selectedOption?.routeId
                      //         ? String(selectedOption.routeId)
                      //         : "";

                      //       field.onChange(nextRouteId);
                      //       handleUpdateVehicleMapping(
                      //         index,
                      //         "routeId",
                      //         nextRouteId
                      //       );
                      //     }}
                      //     placeholder={
                      //       mapping.trackerid
                      //         ? "Please select route"
                      //         : "Select vehicle first"
                      //     }
                      //     isClearable
                      //     isDisabled={
                      //       savingVehicleMappings ||
                      //       loadingRoutes ||
                      //       loadingVehicleRouteData ||
                      //       !mapping.trackerid
                      //     }
                      //     styles={selectStyles}
                      //   />
                      // </div>
                    );
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {vehicleError ? (
          <p className="text-sm text-danger">{vehicleError}</p>
        ) : null}

        <div className="flex justify-end gap-3 border-t border-default-200 pt-4">
          <NeoButton
            className="min-w-[140px] bg-black text-white data-[disabled=true]:bg-black data-[disabled=true]:text-white data-[disabled=true]:opacity-60"
            color="black"
            isLoading={savingVehicleMappings}
            isDisabled={isSaveVehicleDisabled}
            spinner={
              <AiOutlineLoading size="xs" className="animate-spin h-6 w-6" />
            }
            onPress={() => {
              void handleSubmit(handleSaveVehicleMappings)();
            }}
          >
            Save Changes
          </NeoButton>

          <NeoButton
            className="min-w-[140px]"
            color="white"
            onPress={handleCloseVehicleModal}
          >
            Cancel
          </NeoButton>
        </div>
      </div>

      <NeoModal
        onOpen={showConfirmationModal}
        onClose={handleCloseConfirmationModal}
        actionType="SuccessfullModal"
        data={{
          success: confirmationSuccess,
          message: confirmationMessage
        }}
        actionText="OK"
        onModalSubmit={handleCloseConfirmationModal}
      />
    </>
  );
};

const TripTypeChip = ({
  active,
  label,
  disabled,
  onClick
}: {
  active: boolean;
  label: string;
  disabled: boolean;
  onClick: () => void;
}) => {
  return (
    <button
      type="button"
      className={`rounded-full px-4 py-1 text-xs font-medium ${
        active ? "bg-primary text-white" : "bg-default-100 text-default-500"
      }`}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );
};

const selectStyles = {
  control: (base: any) => ({
    ...base,
    minHeight: "48px",
    borderWidth: "2px",
    borderColor: "#d4d4d8",
    borderRadius: "12px",
    paddingLeft: "12px",
    paddingRight: "12px",
    boxShadow: "none",
    fontSize: "14px",
    color: "#111827",
    backgroundColor: "white",
    "&:hover": {
      borderColor: "#d4d4d8"
    }
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "#6b7280",
    fontSize: "14px"
  }),
  indicatorSeparator: () => ({
    display: "none"
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    color: "#6b7280",
    paddingRight: "8px"
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: "10px"
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isFocused ? "#e5e7eb" : "white",
    color: "#111827",
    padding: "10px 12px",
    cursor: "pointer",
    borderRadius: "8px",
    margin: "2px 4px"
  })
};

const getDuplicateVehicleError = (mappings: VehicleMappingItem[]) => {
  const seen = new Set<string>();

  for (const mapping of mappings) {
    if (!mapping.trackerid) continue;

    const key = `${mapping.trackerid}-${mapping.tripType}`;

    if (seen.has(key)) {
      return mapping.tripType === "pickup"
        ? "Same vehicle pickup can be selected only once."
        : "Same vehicle drop can be selected only once.";
    }

    seen.add(key);
  }

  return null;
};

const getDuplicateVehicleIndexes = (mappings: VehicleMappingItem[]) => {
  const seen = new Map<string, number>();
  const duplicateIndexes = new Set<number>();

  mappings.forEach((mapping, index) => {
    if (!mapping.trackerid) return;

    const key = `${mapping.trackerid}-${mapping.tripType}`;

    if (seen.has(key)) {
      duplicateIndexes.add(seen.get(key)!);
      duplicateIndexes.add(index);
      return;
    }

    seen.set(key, index);
  });

  return duplicateIndexes;
};

const getRoutesForVehicle = (
  trackerId: string,
  vehicleRoutes: VehicleRoutes
) => {
  if (!trackerId) return [];
  return vehicleRoutes[String(trackerId)] || [];
};

const getTripTypeValue = (tripType: "pickup" | "drop"): 1 | 2 => {
  return tripType === "pickup" ? 1 : 2;
};

const getVehicleTripTypes = (
  type: UserVehicleType | undefined
): Array<"pickup" | "drop"> => {
  if (type === 1) return ["pickup"];
  if (type === 2) return ["drop"];

  return ["pickup", "drop"];
};

const getVehiclesForTrip = (
  vehicles: UserVehicle[],
  tripType: "pickup" | "drop"
) => {
  return vehicles.filter(vehicle =>
    getVehicleTripTypes(vehicle.type).includes(tripType)
  );
};

const buildVehicleMappings = (
  items: UserDetailsData | undefined
): VehicleMappingItem[] => {
  return (items?.vehicles || []).flatMap((vehicle, index) => {
    const trackerid = String(vehicle.trackerid ?? "");
    const routeId = getVehicleRouteId(vehicle);

    return getVehicleTripTypes(vehicle.type).map(tripType => ({
      id: createVehicleMappingId(tripType, trackerid, routeId, index),
      trackerid,
      routeId,
      tripType
    }));
  });
};

const buildInitialVehicleMappings = (
  items: UserDetailsData | undefined
): VehicleMappingItem[] => {
  const existingMappings = buildVehicleMappings(items).slice(
    0,
    MAX_VEHICLE_MAPPINGS
  );

  if (existingMappings.length > 0) {
    return existingMappings;
  }

  return [
    {
      id: `${Date.now()}-0`,
      trackerid: "",
      routeId: "",
      tripType: "pickup"
    }
  ];
};

const getVehicleRouteId = (vehicle: UserVehicle) => {
  return String(vehicle?.routeId ?? "");
};

const createVehicleMappingId = (
  tripType: "pickup" | "drop",
  trackerid: string,
  routeId: string,
  index: number
) => {
  return `${tripType}-${trackerid || "empty"}-${routeId || "no-route"}-${index}`;
};

const getTrackerName = (vehicleOptions: SelectOption[], trackerId: string) => {
  return vehicleOptions.find(option => option.value === trackerId)?.label;
};

const getRouteName = (routeOptions: SelectOption[], routeId: string) => {
  return routeOptions.find(option => option.value === routeId)?.label;
};

const getMergedVehicleType = (types: Set<1 | 2>): UserVehicleType => {
  if (types.has(1) && types.has(2)) return 3 as UserVehicleType;

  return Array.from(types)[0] as UserVehicleType;
};

const mergeVehicleMappings = (
  mappings: VehicleMappingItem[]
): Array<{trackerid: string; routeId: string; type: UserVehicleType}> => {
  const mergedMap = new Map<
    string,
    {
      trackerid: string;
      routeId: string;
      types: Set<1 | 2>;
    }
  >();

  mappings.forEach(item => {
    if (!item.trackerid) return;

    const routeKey = item.routeId || "no-route";
    const mapKey = `${item.trackerid}-${routeKey}`;

    if (!mergedMap.has(mapKey)) {
      mergedMap.set(mapKey, {
        trackerid: item.trackerid,
        routeId: item.routeId,
        types: new Set()
      });
    }

    mergedMap.get(mapKey)?.types.add(getTripTypeValue(item.tripType));
  });

  return Array.from(mergedMap.values())
    .map(value => ({
      trackerid: value.trackerid,
      routeId: value.routeId,
      type: getMergedVehicleType(value.types)
    }))
    .filter(item => item.trackerid);
};

const buildUpdatedUserData = (
  items: UserDetailsData,
  mappings: VehicleMappingItem[],
  vehicleOptions: SelectOption[],
  routeOptions: SelectOption[]
): UserDetailsData => {
  const existingVehicles = items?.vehicles || [];
  const vehicles = mergeVehicleMappings(mappings).map(item => {
    const existingVehicle = existingVehicles.find(
      vehicle => String(vehicle.trackerid) === item.trackerid
    );
    const selectedRouteName = getRouteName(routeOptions, item.routeId);
    const isSameRoute =
      String(existingVehicle?.routeId ?? "") === String(item.routeId ?? "");

    return {
      ...existingVehicle,
      trackerid: item.trackerid,
      trackerName:
        getTrackerName(vehicleOptions, item.trackerid) ||
        existingVehicle?.trackerName ||
        "NA",
      routeId: item.routeId,
      routeName:
        selectedRouteName ||
        (isSameRoute ? existingVehicle?.routeName : "") ||
        "NA",
      bpName: isSameRoute ? existingVehicle?.bpName || "NA" : "NA",
      type: item.type
    };
  });

  return {
    ...items,
    pickup: getVehiclesForTrip(vehicles, "pickup"),
    drop: getVehiclesForTrip(vehicles, "drop"),
    vehicles
  };
};
